import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { Phone, Mail, Package, Ruler } from "lucide-react";
import { useShipContext } from "../../../../context/shipContext";
import { ShipCard } from "../../../Cards/ShipCard";
import { FormLabel } from "../../FormLabel";
import { AddButton } from "../../../Buttons/AddButton";
import { SecondaryButton } from "../../../Buttons/SecondaryButton";
import { Alert } from "../../../Alerts/Alert";
import { SavePaymentModal } from "../../../Modals/SavePaymentModal";
import { LoadingLayout } from "../../../Layouts/LoadingLayout";
import { SummarySection } from "../../../Sections/Checkout/SummarySection";
import { SelectedCarrierSection } from "../../../Sections/Checkout/SelectedCarrierSection";

import {
  useListPaymentMethods,
  useConfirmShipment,
} from "../../../../api/hooks/index";

import {
  getExistingPaymentMethodOptions,
  createShippingRequest,
} from "../../../../helpers";

import {
  COMPLETE_CHECKOUT_DETAILS_STEP,
  PREVIOUS_STEP,
} from "../../../../reducers/types";
import { checkoutSchema } from "../../validations/ship";
import { Button } from "../../../Buttons/Button";
import { CardSelect } from "../../CardSelect";
import { STRIPE_ICON } from "../../../../assets/icons";

// Modal to show all shipping details

function ShippingDetailsModal({ open, setOpen, shipState }) {
  if (!open) return null;

  const invoice = shipState.item.invoice;
  const hasInvoice = invoice && invoice.length > 0;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div
        data-testid="shipping-details-modal"
        className="bg-white rounded-2xl shadow-xl w-full max-w-3xl p-6 overflow-y-auto max-h-[80vh]"
      >
        <h2 className="text-xl font-semibold mb-6 text-gray-900">
          Shipping Details
        </h2>

        {/* Addresses Section */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          {/* From Address */}
          {/* From Address */}
          <div className="p-4 border rounded-lg bg-gray-50">
            <h4 className="font-semibold text-gray-700 mb-2">From</h4>

            {shipState.from?.isExistingAddress &&
            shipState.from?.existingAddress?.address ? (
              <>
                <p className="font-medium">
                  {shipState.from.existingAddress.address.name}
                </p>
                <p>{shipState.from.existingAddress.address.address1}</p>
                {shipState.from.existingAddress.address.address2 && (
                  <p>{shipState.from.existingAddress.address.address2}</p>
                )}
                <p>
                  {shipState.from.existingAddress.address.city},{" "}
                  {shipState.from.existingAddress.address.state.name},{" "}
                  {shipState.from.existingAddress.address.country.name}{" "}
                  {shipState.from.existingAddress.address.zip}
                </p>
                <div className="mt-2 space-y-1 text-sm text-gray-600">
                  <p className="flex items-center gap-2">
                    <Phone size={14} />{" "}
                    {shipState.from.existingAddress.address.phone}
                  </p>
                  <p className="flex items-center gap-2">
                    <Mail size={14} />{" "}
                    {shipState.from.existingAddress.address.email}
                  </p>
                </div>
              </>
            ) : (
              <>
                <p className="font-medium">{shipState.from.name}</p>
                <p>{shipState.from.address1}</p>
                {shipState.from.address2 && <p>{shipState.from.address2}</p>}
                <p>
                  {shipState.from.city}, {shipState.from.state.name},{" "}
                  {shipState.from.country.name} {shipState.from.zip}
                </p>
                <div className="mt-2 space-y-1 text-sm text-gray-600">
                  <p className="flex items-center gap-2">
                    <Phone size={14} /> {shipState.from.phone}
                  </p>
                  <p className="flex items-center gap-2">
                    <Mail size={14} /> {shipState.from.email}
                  </p>
                </div>
              </>
            )}
          </div>

          {/* To Address */}
          <div className="p-4 border rounded-lg bg-gray-50">
            <h4 className="font-semibold text-gray-700 mb-2">To</h4>
            <p className="font-medium">{shipState.to.name}</p>
            <p>{shipState.to.address1}</p>
            {shipState.to.address2 && <p>{shipState.to.address2}</p>}
            <p>
              {shipState.to.city}, {shipState.to.state.name},{" "}
              {shipState.to.country.name} {shipState.to.zip}
            </p>
            <div className="mt-2 space-y-1 text-sm text-gray-600">
              <p className="flex items-center gap-2">
                <Phone size={14} /> {shipState.to.phone}
              </p>
              <p className="flex items-center gap-2">
                <Mail size={14} /> {shipState.to.email}
              </p>
            </div>
          </div>
        </div>

        {/* Item Details */}
        <div className="p-4 border rounded-lg bg-gray-50 mb-6">
          <h4 className="font-semibold text-gray-700 mb-2 flex items-center gap-2">
            <Package size={16} /> Item Details
          </h4>
          <div className="text-sm text-gray-700 space-y-1">
            <p>Type: {shipState.item.type.name}</p>
            <p>
              Weight: {shipState.item.weight}{" "}
              {shipState.item.isImperial ? "lbs" : "kg"}
            </p>
            <p className="flex items-center gap-1">
              <Ruler size={14} />
              {shipState.item.length} × {shipState.item.width} ×{" "}
              {shipState.item.height} {shipState.item.isImperial ? "in" : "cm"}
            </p>
            {shipState.item.insurance && <p>Insurance: Yes</p>}
            {shipState.item.value && (
              <p>Declared Value: ${shipState.item.value}</p>
            )}
          </div>
        </div>

        {/* Customs Invoice - conditional */}
        {hasInvoice && (
          <div className="p-4 border rounded-lg bg-gray-50 mb-6">
            <h4 className="font-semibold text-gray-700 mb-3 flex items-center gap-2">
              <Package size={16} /> Customs Invoice
            </h4>

            <div className="grid grid-cols-12 gap-2 text-sm text-gray-700 font-medium border-b border-gray-300 pb-2">
              <div className="col-span-5">Description</div>
              <div className="col-span-3">HS Code</div>
              <div className="col-span-2">Origin</div>
              <div className="col-span-2 text-right">Qty × Unit Price</div>
            </div>

            {shipState.item.invoice.map((item, index) => (
              <div
                key={index}
                className="grid grid-cols-12 gap-2 text-sm text-gray-700 py-2 border-b border-gray-100 last:border-b-0"
              >
                <div className="col-span-5">{item.description}</div>
                <div className="col-span-3">{item.hsCode}</div>
                <div className="col-span-2">{item.originCountry?.name}</div>
                <div className="col-span-2 text-right">
                  {item.quantity} × ${item.unitPrice} = $
                  {item.quantity * item.unitPrice}{" "}
                  {shipState.item?.invoiceCurrency.id}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Carrier */}
        <div className="p-4 border rounded-lg bg-gray-50 mb-6">
          <h4 className="font-semibold text-gray-700 mb-3">Selected Carrier</h4>
          <SelectedCarrierSection selectedRate={shipState.selectedRate} />
        </div>

        {/* Actions */}
        <div className="flex justify-end">
          <SecondaryButton text="Close" onClick={() => setOpen(false)}/>
        </div>
      </div>
    </div>
  );
}

export function CheckoutDetailsForm() {
  const [open, setOpen] = useState(false);
  const [shippingModalOpen, setShippingModalOpen] = useState(false);

  const { state: shipState, dispatch } = useShipContext();

  const {
    isLoading: isListPaymentMethodsLoading,
    isError: isListPaymentMethodsError,
    data: paymentMethodsData,
    error: listPaymentMethodsError,
  } = useListPaymentMethods();

  const {
    handleSubmit,
    control,
    setValue,
    formState: { errors },
  } = useForm({
    defaultValues: { selectedPaymentMethod: {} },
    resolver: yupResolver(checkoutSchema),
  });

  const {
    isLoading: isConfirmShipmentLoading,
    isError: isConfirmShipmentError,
    mutateAsync: confirmShipment,
    error: confirmShipmentError,
  } = useConfirmShipment();

  const existingPaymentMethodOptions =
    getExistingPaymentMethodOptions(paymentMethodsData);

  useEffect(() => {
    if (
      existingPaymentMethodOptions &&
      existingPaymentMethodOptions.length > 0
    ) {
      setValue("selectedPaymentMethod", existingPaymentMethodOptions[0]);
    }
  }, [existingPaymentMethodOptions, setValue]);

  const goBack = () => dispatch({ type: PREVIOUS_STEP });

  const onSubmit = async (data) => {
    const shippingRequest = createShippingRequest(
      data.selectedPaymentMethod.id,
      shipState
    );

    try {
      const shippingResponse = await confirmShipment(shippingRequest);
      dispatch({
        type: COMPLETE_CHECKOUT_DETAILS_STEP,
        payload: shippingResponse.order?.orderId,
      });
    } catch (err) {}
  };

  if (isListPaymentMethodsLoading) return <LoadingLayout />;

  return (
    <div data-testid="ship-checkout-step">
      <SavePaymentModal open={open} setOpen={setOpen} />

      <ShipCard>
        {isConfirmShipmentError && confirmShipmentError && (
          <Alert
            code={confirmShipmentError.response?.status}
            message={confirmShipmentError.response?.data?.message}
          />
        )}

        <div className="lg:grid lg:grid-cols-12 lg:gap-x-8 xl:gap-x-12">
          {/* Left Column: Payment + Summary */}
          <section className="lg:col-span-8 flex flex-col gap-6">
            {/* Payment Method */}
            <div className="bg-white rounded-lg shadow p-4 border border-gray-200 space-y-3">
              <div className="flex justify-between items-center mb-2">
                <FormLabel title="Payment Method" />
                <AddButton
                  text="Add a payment method"
                  onClick={() => setOpen(true)}
                />
              </div>
              {isListPaymentMethodsError ? (
                <Alert code={listPaymentMethodsError.response?.status} />
              ) : (
                <>
                  <CardSelect
                    name="selectedPaymentMethod"
                    control={control}
                    options={existingPaymentMethodOptions || []}
                    className="w-full min-w-[250px]"
                  />
                  <div className="mt-2 flex justify-end w-full">
                    {STRIPE_ICON}
                  </div>

                  {errors.selectedPaymentMethod?.id && (
                    <p className="text-red-600 text-sm mt-1">
                      <Alert
                        message={errors.selectedPaymentMethod.id.message}
                      />
                    </p>
                  )}
                </>
              )}
            </div>

            {/* Order Summary */}
            <SummarySection
              selectedRate={shipState.selectedRate}
              onSubmit={handleSubmit(onSubmit)}
              isLoading={isConfirmShipmentLoading}
            />
          </section>

          {/* Right Column: Shipping Details Button */}
          <section className="lg:col-span-4 flex flex-col gap-6">
            <div className="bg-white rounded-lg shadow p-4 border border-gray-200 flex flex-col items-center justify-center h-full">
              <Button
                text={"Review Shipping Details"}
                onClick={() => setShippingModalOpen(true)}
              />
            </div>
          </section>
        </div>
      </ShipCard>

      {/* Back Button */}
      <div className="flex justify-end mt-2">
        <SecondaryButton text="Back" onClick={goBack} />
      </div>

      {/* Shipping Details Modal */}
      <ShippingDetailsModal
        open={shippingModalOpen}
        setOpen={setShippingModalOpen}
        shipState={shipState}
      />
    </div>
  );
}
